import React from 'react';
import { Icon } from '@iconify/react';
import { formatDateDDMMYYYY } from '@/utils/dateUtils';

export interface KlasemenStandingItem {
  position: number;
  playerName: string;
  avatarUrl?: string;
  matches: number;
  wins: number;
  points: number;
  winRate?: number;
  isCurrentUser?: boolean;
}

export interface KlasemenCardProps {
  eventName?: string;
  eventDateFormatted?: string;
  location?: string;
  totalPlayers?: number;
  playerName?: string;
  standings?: KlasemenStandingItem[];
  bgImage?: string;
  transparent?: boolean;
  className?: string;
}

const DEFAULT_BG_IMAGE = 'https://cdn.sporto.id/bgshare.webp';
const SPORTO_LOGO_URL = 'https://cdn.sporto.id/sporto.svg';

/**
 * Truncates player names to keep the leaderboard layout clean.
 * If the name exceeds maxLen characters, extracts only the first word (e.g. "Alam Sutera Suterna" -> "Alam").
 */
export function formatPlayerDisplayName(name?: string, maxLen: number = 14): string {
  if (!name) return 'Pemain';
  const trimmed = name.trim();
  if (trimmed.length <= maxLen) return trimmed;
  const firstWord = trimmed.split(/\s+/)[0];
  if (firstWord.length > maxLen) {
    return firstWord.slice(0, maxLen);
  }
  return firstWord;
}

const getInitials = (name?: string): string => {
  if (!name) return 'P';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2 && parts[0][0] && parts[1][0]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const DUMMY_STANDINGS: KlasemenStandingItem[] = [
  { position: 1, playerName: 'Alfir', matches: 12, wins: 8, points: 148, winRate: 67 },
  { position: 2, playerName: 'Rendra', matches: 12, wins: 7, points: 141, winRate: 58 },
  { position: 3, playerName: 'Yoga', matches: 12, wins: 6, points: 133, winRate: 50 },
  { position: 4, playerName: 'Bagas', matches: 12, wins: 6, points: 128, winRate: 50 },
  { position: 5, playerName: 'Dimas', matches: 12, wins: 5, points: 121, winRate: 42 },
  { position: 10, playerName: 'Rizky', matches: 12, wins: 2, points: 94, winRate: 17, isCurrentUser: true },
];

export const KlasemenCard: React.FC<KlasemenCardProps> = ({
  eventName = 'Padel Americano',
  eventDateFormatted = '19 Nov 2024',
  location = 'Sporto Arena Senayan',
  totalPlayers: _totalPlayers,
  playerName,
  standings = DUMMY_STANDINGS,
  bgImage,
  transparent = true,
  className = '',
}) => {
  const activeBgImage = bgImage || DEFAULT_BG_IMAGE;
  const formattedDate = formatDateDDMMYYYY(eventDateFormatted);
  const dateLoc = `${location || 'Arena Senayan'} · ${formattedDate}`;

  const rawList = standings && standings.length > 0 ? standings : DUMMY_STANDINGS;
  const list = rawList.map((s, idx) => ({
    ...s,
    position: s.position || idx + 1,
  }));

  const isUser = (s?: KlasemenStandingItem) => {
    if (!s) return false;
    if (s.isCurrentUser) return true;
    if (!playerName || playerName === 'Player') return false;
    const p1 = s.playerName.trim().toLowerCase();
    const p2 = playerName.trim().toLowerCase();
    return p1 === p2 || p1.includes(p2) || p2.includes(p1);
  };

  const firstPlace = list.find((s) => s.position === 1) || list[0];
  const otherPlaces = list.filter((s) => s.position > 1 && s.position <= 5);

  const currentUserRow = list.find((s) => isUser(s));
  const showCurrentUserSeparately = Boolean(currentUserRow && currentUserRow.position > 5);

  const firstPlaceWinRate =
    firstPlace?.winRate ??
    (firstPlace && firstPlace.matches > 0
      ? Math.round((firstPlace.wins / firstPlace.matches) * 100)
      : 0);

  return (
    <div
      className={`relative w-[540px] h-[960px] overflow-hidden select-none ${
        transparent ? 'bg-transparent' : 'bg-[#0E1726] shadow-[0_8px_30px_rgba(14,23,38,0.18)]'
      } ${className}`}
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      {/* Background Image & Overlay (only when not transparent) */}
      {!transparent && (
        <>
          <img
            src={activeBgImage}
            alt="Foto latar pertandingan"
            className="absolute inset-0 w-full h-full object-cover object-[50%_35%]"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.25) 30%, rgba(0,0,0,0.65) 60%, rgba(0,0,0,0.92) 100%)',
            }}
          />
        </>
      )}

      {/* Content Container */}
      <div className="absolute inset-0 flex flex-col justify-end p-[28px_24px_24px]">
        {/* Dark Glassmorphic Card Container matching Admin Download Design */}
        <div
          className="relative rounded-[24px] p-[24px_22px_22px] overflow-hidden shadow-2xl"
          style={{
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'linear-gradient(180deg, rgba(20,20,22,0.72) 0%, rgba(12,12,14,0.92) 100%)',
            backdropFilter: 'blur(16px)',
          }}
        >
          {/* Header Info */}
          <div className="flex items-start justify-between gap-[16px] mb-[18px]">
            <div className="flex flex-col gap-[2px] min-w-0">
              <span
                className="text-[12px] font-black tracking-[0.14em] text-[#FC480D] uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                KLASEMEN AKHIR
              </span>
              <h2 className="text-[26px] font-extrabold text-white leading-[30px] tracking-[-0.02em] uppercase truncate">
                {eventName}
              </h2>
              <p className="text-[13px] font-medium text-white/60 truncate mt-0.5">
                {dateLoc}
              </p>
            </div>

            {/* Trophy Circular Badge */}
            <div className="w-[42px] h-[42px] rounded-full border border-white/20 bg-white/10 flex items-center justify-center text-white shrink-0 shadow-sm">
              <Icon icon="ph:trophy-bold" className="w-[22px] h-[22px] text-white" />
            </div>
          </div>

          {/* Spotlight Box for Rank 1 Player */}
          {firstPlace && (
            <div
              className="rounded-[18px] p-[14px_16px] flex items-center justify-between gap-[14px] mb-[16px]"
              style={
                isUser(firstPlace)
                  ? {
                      background: 'rgba(252, 72, 13, 0.15)',
                      border: '1px solid rgba(252, 72, 13, 0.6)',
                    }
                  : {
                      background: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                    }
              }
            >
              <div className="flex items-center gap-[12px] min-w-0 flex-1">
                {/* Avatar with Rank Badge */}
                <div className="shrink-0 relative">
                  {firstPlace.avatarUrl ? (
                    <img
                      src={firstPlace.avatarUrl}
                      alt={firstPlace.playerName}
                      className="w-[52px] h-[52px] rounded-full object-cover border-2 border-white/20 shadow-md"
                    />
                  ) : (
                    <div className="w-[52px] h-[52px] rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-extrabold text-[17px] border-2 border-white/20 shadow-md">
                      {getInitials(firstPlace.playerName)}
                    </div>
                  )}
                  <div className="absolute -bottom-1 -right-1 w-[20px] h-[20px] rounded-full bg-[#FC480D] text-white flex items-center justify-center text-[10px] font-black border border-white shadow-xs">
                    1
                  </div>
                </div>

                {/* Name & Subtitle Stats */}
                <div className="min-w-0 flex-1">
                  <h3 className="text-[17px] font-extrabold text-white leading-[22px] truncate">
                    {formatPlayerDisplayName(firstPlace.playerName, 16)}
                  </h3>
                  <p className="text-[12px] font-medium text-white/65 truncate mt-[3px]">
                    {firstPlace.matches} Main · {firstPlace.wins} Menang · {firstPlaceWinRate}% WR
                  </p>
                </div>
              </div>

              {/* Big Stat Value */}
              <div className="text-right shrink-0 flex flex-col items-end">
                <span className="text-[32px] font-black text-white leading-none tracking-tight">
                  {firstPlace.points}
                </span>
                <span className="text-[10px] font-black text-[#FC480D] uppercase tracking-wider mt-[4px]">
                  POIN
                </span>
              </div>
            </div>
          )}

          {/* Table Rows for Rank 2 - 5 */}
          <div className="flex flex-col gap-[2px]">
            {/* Table Header */}
            <div className="flex items-center gap-[10px] px-[8px] pb-[8px] border-b border-white/10">
              <div
                className="w-[20px] text-[11px] font-bold tracking-[0.1em] text-white/40"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                #
              </div>
              <div
                className="flex-1 text-[11px] font-bold tracking-[0.1em] text-white/40 uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                PEMAIN
              </div>
              <div
                className="w-[36px] text-center text-[11px] font-bold tracking-[0.1em] text-white/40 uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                M
              </div>
              <div
                className="w-[36px] text-center text-[11px] font-bold tracking-[0.1em] text-white/40 uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                W
              </div>
              <div
                className="w-[44px] text-center text-[11px] font-bold tracking-[0.1em] text-white/40 uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                WR
              </div>
              <div
                className="w-[52px] text-right text-[11px] font-bold tracking-[0.1em] text-white/40 uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                POIN
              </div>
            </div>

            {/* Rows for Positions 2 to 5 */}
            {otherPlaces.map((row, idx) => {
              const isCurrent = isUser(row);
              const rank = row.position || idx + 2;
              const winRate =
                row.winRate ?? (row.matches > 0 ? Math.round((row.wins / row.matches) * 100) : 0);
              const displayName = formatPlayerDisplayName(row.playerName, 14);

              return (
                <div
                  key={`${row.playerName}-${row.position}-${idx}`}
                  className={`flex items-center gap-[10px] px-[8px] py-[9px] ${
                    isCurrent
                      ? 'rounded-[12px] my-[2px] p-[9px_8px]'
                      : 'border-b border-white/5 last:border-b-0'
                  }`}
                  style={
                    isCurrent
                      ? {
                          background: 'rgba(252,72,13,0.14)',
                          border: '1px solid rgba(252,72,13,0.5)',
                        }
                      : undefined
                  }
                >
                  {/* Rank */}
                  <div
                    className={`w-[20px] text-[14px] font-extrabold ${
                      isCurrent ? 'text-[#FC480D]' : 'text-white/80'
                    }`}
                  >
                    {rank}
                  </div>

                  {/* Player Name */}
                  <div className="flex-1 min-w-0">
                    <span className="text-[14px] font-bold text-white truncate block">
                      {displayName}
                    </span>
                  </div>

                  {/* Matches (M) */}
                  <div
                    className={`w-[36px] text-center text-[13px] font-semibold ${
                      isCurrent ? 'text-white/95' : 'text-white/70'
                    }`}
                  >
                    {row.matches}
                  </div>

                  {/* Wins (W) */}
                  <div
                    className={`w-[36px] text-center text-[13px] font-semibold ${
                      isCurrent ? 'text-white/95' : 'text-white/70'
                    }`}
                  >
                    {row.wins}
                  </div>

                  {/* Winrate (WR) */}
                  <div
                    className={`w-[44px] text-center text-[13px] font-semibold ${
                      isCurrent ? 'text-white/95' : 'text-white/70'
                    }`}
                  >
                    {winRate}%
                  </div>

                  {/* Poin */}
                  <div
                    className={`w-[52px] text-right text-[15px] font-black ${
                      isCurrent ? 'text-[#FC480D]' : 'text-white'
                    }`}
                  >
                    {row.points}
                  </div>
                </div>
              );
            })}

            {/* Current user row if outside top 5 */}
            {showCurrentUserSeparately && currentUserRow && (
              <>
                <div className="flex items-center justify-center p-[6px_8px] border-b border-white/8">
                  <div className="text-[13px] font-bold tracking-[0.3em] text-white/30">
                    ···
                  </div>
                </div>
                {(() => {
                  const winRate =
                    currentUserRow.winRate ??
                    (currentUserRow.matches > 0
                      ? Math.round((currentUserRow.wins / currentUserRow.matches) * 100)
                      : 0);
                  const displayName = formatPlayerDisplayName(currentUserRow.playerName, 14);
                  return (
                    <div
                      className="flex items-center gap-[10px] rounded-[12px] p-[9px_8px] my-[2px]"
                      style={{
                        background: 'rgba(252,72,13,0.14)',
                        border: '1px solid rgba(252,72,13,0.5)',
                      }}
                    >
                      <div className="w-[20px] text-[14px] font-extrabold text-[#FC480D]">
                        {currentUserRow.position}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[14px] font-bold text-white truncate block">
                          {displayName}
                        </span>
                      </div>
                      <div className="w-[36px] text-center text-[13px] font-semibold text-white/95">
                        {currentUserRow.matches}
                      </div>
                      <div className="w-[36px] text-center text-[13px] font-semibold text-white/95">
                        {currentUserRow.wins}
                      </div>
                      <div className="w-[44px] text-center text-[13px] font-semibold text-white/95">
                        {winRate}%
                      </div>
                      <div className="w-[52px] text-right text-[15px] font-black text-[#FC480D]">
                        {currentUserRow.points}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </div>

        {/* Bottom Logo */}
        <div className="flex justify-center pt-[18px]">
          <img src={SPORTO_LOGO_URL} alt="Sporto" className="h-[24px] w-auto object-contain block opacity-95" />
        </div>
      </div>
    </div>
  );
};

export default KlasemenCard;
